import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IAuthorization } from '~/types/authorization'

import columns from './columns'
import CustomTooltip from './CustomTooltip'

import ManageAuthorizationModal from '../ManageAuthorizationModal'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { keys as authorizationsKeys, useAuthorizationsListQuery } from '~/queries/iam-authorizations'
import { dateSort } from '~/utils/date'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const AuthorizationListing = (): ReactElement => {
  /** Local state */

  const authorizationsWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.AUTH.AUTHORIZATIONS.WRITE])

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact<IAuthorization>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isModalOpen, setModalOpen] = useState(false)

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  /** Queries & Mutations */

  const { data: authorizations } = useAuthorizationsListQuery()

  const sortAuthorization = authorizations?.data?.sort(
    ({ updated_at_ts: aUpdatedDate }, { updated_at_ts: bUpdatedDate }) => dateSort(aUpdatedDate, bUpdatedDate, 'desc')
  )

  /** Effects */

  useDocumentTitle('IAM Authorizations')

  /** Handlers */

  const handleOpenModal = (isOpen: boolean) => () => setModalOpen(isOpen)

  const handleSuccess = () => {
    setModalOpen(false)
    queryClient.invalidateQueries({ queryKey: authorizationsKeys.list() })
    displayToast('Authorization successfully created', 'success')
  }

  const handleFailure = (error: unknown) => {
    setModalOpen(false)

    if (isAxiosError(error)) {
      switch (error.response?.data.code) {
        case 409:
          displayToast('Authorization could not be created; authorization key is already forged', 'error')
          break
        default:
          captureException(error)
          displayToast('Authorization could not be created', 'error')
      }
    } else {
      captureException(error)
      displayToast('Authorization could not be created', 'error')
    }
  }

  const handleRowClicked = (evt: RowClickedEvent) => {
    if (isTouchDown) {
      window.open(`/mwm-users/authorizations/${evt.data.key}`, '_blank', 'rel=noopener noreferrer')
      setIsTouchDown(false)
    } else {
      navigate(`/mwm-users/authorizations/${evt.data.key}`)
    }
  }

  /** Render */

  return (
    <>
      <Layout className="authorizations-view" withPadding>
        <ViewTitle className="layout-view-title">IAM Authorizations</ViewTitle>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!authorizationsWriteAuthorized}
            addButtonLabel="New authorization"
            addButtonTooltip={!authorizationsWriteAuthorized ? 'unauthorized' : undefined}
            className="authorizations-table with-min-height"
            columnDefs={columns}
            components={{ customTooltip: CustomTooltip }}
            entriesName="authorizations"
            onAddButtonClick={handleOpenModal(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={sortAuthorization}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      {authorizationsWriteAuthorized ? (
        <ManageAuthorizationModal
          isOpen={isModalOpen}
          onClose={handleOpenModal(false)}
          onFailure={handleFailure}
          onSuccess={handleSuccess}
        />
      ) : null}
    </>
  )
}

export default AuthorizationListing
